import { create, type StateCreator } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { tabStorage } from "./tabStorage";
import { assistantErrorEntry, type AssistantErrorEntry } from "@/api/form-builder/assistantError";
import { requestFormAssistant } from "@/api/form-builder/formBuilder.mutations";
import {
  DroppedRepeatingGroupsError,
  pagesToDraft,
  specToPages,
} from "@/components/features/FormBuilder/utils/specToPages";
import type {
  AssistantChatMessage,
  AssistantFormSpec,
  AssistantPlan,
  AssistantRequest,
  AssistantResponse,
} from "@/api/form-builder/formAssistant.types";
import type { FormPage } from "@/components/features/FormBuilder/types/form.types";

export type AssistantPhase =
  | "idle"
  | "thinking"
  | "awaitingApproval"
  | "building"
  | "review"
  | "confirmChange";

export type PlanStatus = "pending" | "approved" | "cancelled" | "replaced";
export type ChangeStatus = "pending" | "confirmed" | "cancelled";

export type AssistantEntry =
  | {
      id: number;
      kind: "user" | "assistant" | "note";
      text: string;
      // Sent to the model instead of `text` when the screen asked on the person's
      // behalf (a request handed over from the assignments assistant): the chat
      // shows what they typed, the model gets the full brief.
      prompt?: string;
    }
  | ({ id: number } & AssistantErrorEntry)
  | { id: number; kind: "plan"; plan: AssistantPlan; status: PlanStatus }
  | { id: number; kind: "change"; summary: string; spec: AssistantFormSpec; status: ChangeStatus };

type NewAssistantEntry = AssistantEntry extends infer Entry
  ? Entry extends AssistantEntry
    ? Omit<Entry, "id">
    : never
  : never;

export interface AssistantProposal {
  spec: AssistantFormSpec;
  pages: FormPage[];
  // The builder's draft JSON when the proposal was made; Apply is refused if
  // the draft moved since, so nothing the person did by hand is overwritten.
  baseDraft: string;
  basePages: FormPage[];
}

export interface AssistantTurnContext {
  tenantId?: string;
  draftJSON?: string;
  draftPages?: FormPage[];
}

interface AssistantState {
  isOpen: boolean;
  /** The form this chat belongs to: its id, or the new-form page it was started on. */
  formKey: string | null;
  phase: AssistantPhase;
  messages: AssistantEntry[];
  proposal: AssistantProposal | null;
  stale: boolean;
  /** Set when a plan approved elsewhere should be built as soon as the panel opens. */
  buildOnOpen: boolean;
  /** A change confirmed in the assignments assistant, proposed as soon as the panel opens. */
  requestOnOpen: { prompt: string; said?: string } | null;
  setOpen: (open: boolean) => void;
  /** Keeps the chat when the same form opens again (a reload); another form starts clean. */
  bindTo: (formKey: string) => void;
  /** A new form got its id on its first save: still the same form and the same chat. */
  rebind: (formKey: string) => void;
  /** `said` is what the person typed; the chat shows it, the model gets the brief. */
  startFromPlan: (brief: string, plan: AssistantPlan, said?: string) => void;
  startFromRequest: (prompt: string, said?: string) => void;
  runRequestOnOpen: (context: AssistantTurnContext) => Promise<void>;
  send: (text: string, context: AssistantTurnContext, shownAs?: string) => Promise<void>;
  approvePlan: (context: AssistantTurnContext) => Promise<void>;
  changePlan: () => void;
  cancelPlan: () => void;
  confirmChange: () => void;
  cancelChange: () => void;
  discardProposal: () => void;
  applyProposal: (currentDraft: string) => FormPage[] | null;
  stop: () => void;
  reset: () => void;
}

type RequestFn = (payload: AssistantRequest, signal?: AbortSignal) => Promise<AssistantResponse>;

const MAX_HISTORY = 20;
const APPROVED_PLAN_REQUEST = "Build the form from the plan I approved.";

function toHistory(messages: AssistantEntry[], next: string): AssistantChatMessage[] {
  const history: AssistantChatMessage[] = [];
  for (const entry of messages) {
    if (entry.kind === "user") history.push({ role: "user", content: entry.prompt ?? entry.text });
    if (entry.kind === "assistant") history.push({ role: "assistant", content: entry.text });
  }
  history.push({ role: "user", content: next });
  const recent = history.slice(-MAX_HISTORY);
  while (recent.length > 1 && recent[0].role !== "user") recent.shift();
  return recent;
}

function isAbort(error: unknown): boolean {
  const name = (error as { name?: string })?.name;
  return name === "CanceledError" || name === "AbortError";
}

const initialState = {
  phase: "idle" as AssistantPhase,
  messages: [] as AssistantEntry[],
  proposal: null as AssistantProposal | null,
  stale: false,
  buildOnOpen: false,
  requestOnOpen: null as AssistantState["requestOnOpen"],
};

const INTERRUPTED_NOTE =
  "The page reloaded before the assistant answered. Nothing was changed. Ask again.";

type SavedAssistant = Pick<
  AssistantState,
  | "isOpen"
  | "formKey"
  | "phase"
  | "messages"
  | "proposal"
  | "stale"
  | "buildOnOpen"
  | "requestOnOpen"
>;

/**
 * A chat saved mid-request comes back ready for the next step: the request is
 * gone with the page, so a plan being built waits for approval again and the
 * person is told to ask again.
 */
export function restoreAssistant(saved: SavedAssistant): SavedAssistant {
  if (saved.phase !== "thinking" && saved.phase !== "building") return saved;
  let messages = saved.messages;
  if (saved.phase === "building") {
    const lastPlan = [...messages].reverse().find((m) => m.kind === "plan");
    if (lastPlan?.kind === "plan" && lastPlan.status === "approved") {
      messages = messages.map((m) => (m === lastPlan ? { ...lastPlan, status: "pending" } : m));
    }
  }
  const nextId = Math.max(0, ...messages.map((m) => m.id)) + 1;
  messages = [...messages, { id: nextId, kind: "note", text: INTERRUPTED_NOTE }];
  const pending = (kind: "plan" | "change") =>
    messages.some((m) => m.kind === kind && m.status === "pending");
  const phase: AssistantPhase = pending("change")
    ? "confirmChange"
    : saved.proposal
      ? "review"
      : pending("plan")
        ? "awaitingApproval"
        : "idle";
  return { ...saved, messages, phase };
}

export function createAssistantStore(
  request: RequestFn = requestFormAssistant,
  persistAs?: string,
) {
  let nextId = 1;
  let controller: AbortController | null = null;
  // Bumped by reset so a request still in flight cannot write into a new chat.
  let generation = 0;

  const initializer: StateCreator<AssistantState> = (set, get) => {
    const push = (entry: NewAssistantEntry) =>
      set((state) => ({
        messages: [...state.messages, { ...entry, id: nextId++ } as AssistantEntry],
      }));

    const setStatus = <Kind extends "plan" | "change">(
      kind: Kind,
      from: Extract<AssistantEntry, { kind: Kind }>["status"],
      to: Extract<AssistantEntry, { kind: Kind }>["status"],
    ) =>
      set((state) => ({
        messages: state.messages.map((m) =>
          m.kind === kind && m.status === from ? ({ ...m, status: to } as AssistantEntry) : m,
        ),
      }));

    // Moves one plan by its entry id: other plans in the chat keep their status.
    const setPlanStatus = (entryId: number, to: PlanStatus) =>
      set((state) => ({
        messages: state.messages.map((m) =>
          m.id === entryId && m.kind === "plan" ? { ...m, status: to } : m,
        ),
      }));

    const findPending = <Kind extends "plan" | "change">(kind: Kind) =>
      get().messages.find(
        (m): m is Extract<AssistantEntry, { kind: Kind }> =>
          m.kind === kind && m.status === "pending",
      );

    // A proposal that would drop a repeating group is never offered: the person
    // is told and the form stays as it is.
    const toProposal = (
      spec: AssistantFormSpec,
      baseDraft: string,
      basePages: FormPage[],
    ): AssistantProposal | null => {
      try {
        return { spec, pages: specToPages(spec, basePages), baseDraft, basePages };
      } catch (error) {
        if (!(error instanceof DroppedRepeatingGroupsError)) throw error;
        push({
          kind: "error",
          text: `This would remove the repeating group${error.labels.length > 1 ? "s" : ""} ${error.labels.map((label) => `"${label}"`).join(", ")}, which the assistant cannot rebuild. Nothing was changed; edit ${error.labels.length > 1 ? "those questions" : "that question"} by hand.`,
        });
        return null;
      }
    };

    async function run(
      payload: AssistantRequest,
      busyPhase: AssistantPhase,
      onOk: (response: AssistantResponse) => void,
    ) {
      const previous = get().phase;
      const turn = generation;
      controller?.abort();
      controller = new AbortController();
      const ownController = controller;
      set({ phase: busyPhase });
      try {
        const response = await request(payload, ownController.signal);
        if (turn !== generation) return;
        if (response.outcome === "ok") {
          onOk(response);
        } else {
          push({ kind: "assistant", text: response.reply });
          set({ phase: previous });
        }
      } catch (error) {
        if (turn !== generation) return;
        push(
          isAbort(error)
            ? { kind: "note", text: "Stopped. Nothing was changed." }
            : assistantErrorEntry(error),
        );
        set({ phase: previous });
      } finally {
        if (controller === ownController) controller = null;
      }
    }

    return {
      ...initialState,
      isOpen: false,
      formKey: null,
      setOpen: (open) => set({ isOpen: open }),

      bindTo: (formKey) => {
        if (get().formKey === formKey) return;
        get().reset();
        set({ formKey });
      },

      rebind: (formKey) => set({ formKey }),

      startFromPlan: (brief, plan, said) => {
        push(said ? { kind: "user", text: said, prompt: brief } : { kind: "user", text: brief });
        push({ kind: "plan", plan, status: "pending" });
        set({ isOpen: true, phase: "awaitingApproval", buildOnOpen: true });
      },

      startFromRequest: (prompt, said) => set({ isOpen: true, requestOnOpen: { prompt, said } }),

      // The person asked for this change and confirmed its details in the
      // assignments assistant, so it is planned and built straight away; what
      // they decide on here is the result, shown in the preview to apply or discard.
      runRequestOnOpen: async (context) => {
        const request = get().requestOnOpen;
        if (!request) return;
        set({ requestOnOpen: null });
        await get().send(request.prompt, context, request.said);
        if (get().phase === "awaitingApproval") await get().approvePlan(context);
      },

      send: async (text, context, shownAs) => {
        const { proposal, messages } = get();
        const history = toHistory(messages, text);
        setStatus("plan", "pending", "replaced");
        setStatus("change", "pending", "cancelled");
        push(shownAs ? { kind: "user", text: shownAs, prompt: text } : { kind: "user", text });

        if (proposal) {
          await run(
            {
              mode: "change",
              tenantId: context.tenantId,
              messages: history,
              currentForm: proposal.spec,
            },
            "thinking",
            (response) => {
              push({ kind: "assistant", text: response.reply });
              if (!response.form) return set({ phase: "review" });
              push({
                kind: "change",
                summary: response.changeSummary || response.reply,
                spec: response.form,
                status: "pending",
              });
              set({ phase: "confirmChange" });
            },
          );
          return;
        }

        await run(
          {
            mode: "plan",
            tenantId: context.tenantId,
            messages: history,
            currentDraft: pagesToDraft(context.draftPages ?? []),
          },
          "thinking",
          (response) => {
            push({ kind: "assistant", text: response.reply });
            if (!response.plan) return set({ phase: "idle" });
            push({ kind: "plan", plan: response.plan, status: "pending" });
            set({ phase: "awaitingApproval" });
          },
        );
        if (get().phase === "awaitingApproval" && !findPending("plan")) set({ phase: "idle" });
      },

      approvePlan: async (context) => {
        const plan = findPending("plan");
        set({ buildOnOpen: false });
        if (!plan) return;
        const history = toHistory(get().messages, APPROVED_PLAN_REQUEST);
        setPlanStatus(plan.id, "approved");
        await run(
          {
            mode: "spec",
            tenantId: context.tenantId,
            messages: history,
            approvedPlan: plan.plan,
            currentDraft: pagesToDraft(context.draftPages ?? []),
          },
          "building",
          (response) => {
            push({ kind: "assistant", text: response.reply });
            if (!response.form) return set({ phase: "awaitingApproval" });
            const proposal = toProposal(
              response.form,
              context.draftJSON ?? "",
              context.draftPages ?? [],
            );
            if (!proposal) {
              setPlanStatus(plan.id, "cancelled");
              return set({ phase: "idle" });
            }
            set({ phase: "review", stale: false, proposal });
          },
        );
        // Only this plan goes back to waiting; earlier approved plans stay approved.
        if (get().phase === "awaitingApproval") setPlanStatus(plan.id, "pending");
      },

      changePlan: () => {
        if (!findPending("plan")) return;
        push({
          kind: "assistant",
          text: "What should change? Tell me and I will send a new plan for you to approve.",
        });
        set({ phase: "idle" });
      },

      cancelPlan: () => {
        const plan = findPending("plan");
        if (plan) setPlanStatus(plan.id, "cancelled");
        push({ kind: "note", text: "Plan cancelled. Nothing was built." });
        set({ phase: "idle" });
      },

      // The draft baseline stays the one the first proposal was built on, so hand
      // edits made since then still block Apply after a confirmed change.
      confirmChange: () => {
        const change = findPending("change");
        const proposal = get().proposal;
        if (!change || !proposal) return;
        const next = toProposal(change.spec, proposal.baseDraft, proposal.basePages);
        setStatus("change", "pending", next ? "confirmed" : "cancelled");
        set({ phase: "review", ...(next ? { proposal: next } : {}) });
      },

      cancelChange: () => {
        setStatus("change", "pending", "cancelled");
        set({ phase: "review" });
      },

      discardProposal: () => {
        push({ kind: "note", text: "Proposal discarded. Your form was not changed." });
        set({ proposal: null, stale: false, phase: "idle" });
      },

      applyProposal: (currentDraft) => {
        const { proposal } = get();
        if (!proposal) return null;
        if (proposal.baseDraft !== currentDraft) {
          set({ stale: true });
          return null;
        }
        push({ kind: "note", text: "You applied the proposal to the form." });
        set({ proposal: null, stale: false, phase: "idle" });
        return proposal.pages;
      },

      stop: () => controller?.abort(),

      reset: () => {
        generation += 1;
        controller?.abort();
        set({ ...initialState });
      },
    };
  };

  if (!persistAs) return create<AssistantState>()(initializer);
  // Per browser tab: a reload keeps the chat and the proposal, another tab starts clean.
  return create<AssistantState>()(
    persist(initializer, {
      name: persistAs,
      storage: createJSONStorage(() => tabStorage),
      partialize: ({
        isOpen,
        formKey,
        phase,
        messages,
        proposal,
        stale,
        buildOnOpen,
        requestOnOpen,
      }): SavedAssistant => ({
        isOpen,
        formKey,
        phase,
        messages,
        proposal,
        stale,
        buildOnOpen,
        requestOnOpen,
      }),
      merge: (saved, current) => {
        if (!saved) return current;
        const restored = restoreAssistant(saved as SavedAssistant);
        nextId = Math.max(0, ...restored.messages.map((m) => m.id)) + 1;
        return { ...current, ...restored };
      },
    }),
  );
}

export const useAssistantStore = createAssistantStore(
  requestFormAssistant,
  "form-builder-assistant",
);
